import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Workflow, Plus, Play, Pause, Trash2, Clock, Mail, ChevronRight,
  Loader2, Users, X, GripVertical, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import useConfirm from '@/hooks/use-confirm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITIONS = [
  { value: 'always', label: 'Her zaman gönder' },
  { value: 'not_opened', label: 'Önceki maili AÇMADIYSA' },
  { value: 'opened', label: 'Önceki maili AÇTIYSA' },
  { value: 'not_clicked', label: 'Önceki maile TIKLAMADIYSA' },
  { value: 'clicked', label: 'Önceki maile TIKLADIYSA' },
];

const emptyStep = () => ({
  template_id: '',
  delay_days: 1,
  delay_hours: 0,
  condition: 'always',
});

const Automations = () => {
  const { confirm, confirmDialog } = useConfirm();
  const [automations, setAutomations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {..otomasyon}
  const [saving, setSaving] = useState(false);
  const [enrollTarget, setEnrollTarget] = useState(null);
  const [enrollPreset, setEnrollPreset] = useState('all');
  const [presets, setPresets] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [autoRes, tplRes, presetRes] = await Promise.all([
        axios.get(`${API}/automations`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/recipients/segment-presets`),
      ]);
      setAutomations(autoRes.data || []);
      setTemplates(tplRes.data || []);
      setPresets(presetRes.data.presets || []);
    } catch (error) {
      toast.error('Otomasyonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const startCreate = () => {
    setEditing({
      name: '',
      description: '',
      trigger: { type: 'recipient_added', group: '' },
      steps: [{ ...emptyStep(), delay_days: 0 }],
    });
  };

  const updateStep = (index, patch) => {
    const steps = [...editing.steps];
    steps[index] = { ...steps[index], ...patch };
    setEditing({ ...editing, steps });
  };

  const addStep = () => setEditing({ ...editing, steps: [...editing.steps, emptyStep()] });

  const removeStep = (index) =>
    setEditing({ ...editing, steps: editing.steps.filter((_, i) => i !== index) });

  const handleSave = async () => {
    if (!editing.name.trim()) {
      toast.error('Otomasyona bir ad verin');
      return;
    }
    if (!editing.steps.length) {
      toast.error('En az bir adım ekleyin');
      return;
    }
    if (editing.steps.some((s) => !s.template_id)) {
      toast.error('Her adım için bir şablon seçin');
      return;
    }

    const payload = {
      name: editing.name.trim(),
      description: editing.description || '',
      trigger: {
        type: editing.trigger.type,
        group: editing.trigger.group?.trim() || null,
      },
      steps: editing.steps.map((s) => ({
        template_id: s.template_id,
        delay_days: Number(s.delay_days) || 0,
        delay_hours: Number(s.delay_hours) || 0,
        condition: s.condition || 'always',
      })),
    };

    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/automations/${editing.id}`, payload);
        toast.success('Otomasyon güncellendi');
      } else {
        await axios.post(`${API}/automations`, payload);
        toast.success('Otomasyon oluşturuldu — açmak için "Başlat"a basın');
      }
      setEditing(null);
      await fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (automation) => {
    try {
      const { data } = await axios.post(`${API}/automations/${automation.id}/toggle`);
      toast.success(data.message);
      await fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDelete = (automation) => {
    confirm({
      title: 'Otomasyon silinsin mi?',
      description:
        `"${automation.name}" silinecek ve akışta bekleyen ` +
        `${automation.stats?.active || 0} alıcının akışı durdurulacak.\n\n` +
        'Gönderilmiş mailler etkilenmez.',
      confirmLabel: 'Sil',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/automations/${automation.id}`);
          toast.success('Otomasyon silindi');
          await fetchAll();
        } catch (error) {
          toast.error('Silinemedi');
        }
      },
    });
  };

  const handleEnroll = async () => {
    const preset = presets.find((p) => p.key === enrollPreset);
    try {
      const { data } = await axios.post(`${API}/automations/${enrollTarget.id}/enroll`, {
        segment: preset?.query || {},
      });
      toast.success(data.message);
      setEnrollTarget(null);
      await fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Alıcılar eklenemedi');
    }
  };

  const templateName = (id) => templates.find((t) => t.id === id)?.name || 'Şablon seçilmedi';

  const describeDelay = (step, index) => {
    const d = Number(step.delay_days) || 0;
    const h = Number(step.delay_hours) || 0;
    if (d === 0 && h === 0) return index === 0 ? 'Hemen' : 'Bir önceki adımdan hemen sonra';
    const parts = [];
    if (d) parts.push(`${d} gün`);
    if (h) parts.push(`${h} saat`);
    return `${parts.join(' ')} sonra`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-[#6366f1]" />
      </div>
    );
  }

  // ----------------------------------------------------------- düzenleyici
  if (editing) {
    return (
      <div className="p-6 md:p-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-1">
            {editing.id ? 'Otomasyonu Düzenle' : 'Yeni Otomasyon'}
          </h1>
          <p className="text-[#64748b]">
            Alıcı akışa girdikten sonra maillerin sırayla ve koşullu gönderilmesini sağlar.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-[#e2e8f0] p-5 space-y-5 mb-6">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">Ad</label>
            <input
              type="text"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder="Örn: Hoş Geldin Serisi"
              className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">
              Açıklama (opsiyonel)
            </label>
            <input
              type="text"
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-1">
                Akış ne zaman başlasın?
              </label>
              <select
                value={editing.trigger.type}
                onChange={(e) =>
                  setEditing({ ...editing, trigger: { ...editing.trigger, type: e.target.value } })
                }
                className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg bg-white"
              >
                <option value="recipient_added">Yeni alıcı eklendiğinde (otomatik)</option>
                <option value="manual">Sadece elle eklediğimde</option>
              </select>
            </div>

            {editing.trigger.type === 'recipient_added' && (
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">
                  Sadece bu gruptakiler (boş = hepsi)
                </label>
                <input
                  type="text"
                  value={editing.trigger.group || ''}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      trigger: { ...editing.trigger, group: e.target.value },
                    })
                  }
                  placeholder="Örn: adaylar"
                  className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                />
              </div>
            )}
          </div>
        </div>

        {/* Adımlar */}
        <div className="space-y-3 mb-6">
          {editing.steps.map((step, index) => (
            <div key={index} className="bg-white rounded-xl border border-[#e2e8f0] p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <GripVertical size={16} className="text-[#cbd5e1]" />
                  <span className="w-7 h-7 rounded-full bg-[#6366f1] text-white text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                  <span className="font-semibold text-[#0f172a]">{index + 1}. Adım</span>
                </div>
                {editing.steps.length > 1 && (
                  <button
                    onClick={() => removeStep(index)}
                    className="p-1.5 text-[#94a3b8] hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#64748b] mb-1">Şablon</label>
                  <select
                    value={step.template_id}
                    onChange={(e) => updateStep(index, { template_id: e.target.value })}
                    className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg bg-white"
                  >
                    <option value="">Şablon seçin...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#64748b] mb-1">
                    {index === 0 ? 'Akışa girdikten ne kadar sonra?' : 'Önceki adımdan ne kadar sonra?'}
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input
                        type="number"
                        min="0"
                        value={step.delay_days}
                        onChange={(e) => updateStep(index, { delay_days: e.target.value })}
                        className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg"
                      />
                      <span className="text-xs text-[#94a3b8]">gün</span>
                    </div>
                    <div className="flex-1">
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={step.delay_hours}
                        onChange={(e) => updateStep(index, { delay_hours: e.target.value })}
                        className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg"
                      />
                      <span className="text-xs text-[#94a3b8]">saat</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#64748b] mb-1">Koşul</label>
                  <select
                    value={step.condition}
                    onChange={(e) => updateStep(index, { condition: e.target.value })}
                    disabled={index === 0}
                    className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg bg-white disabled:bg-[#f8fafc] disabled:text-[#94a3b8]"
                  >
                    {CONDITIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-[#94a3b8] mt-1">
                    {index === 0
                      ? 'İlk adımda koşul yoktur'
                      : 'Koşul sağlanmazsa bu adım atlanır, akış devam eder'}
                  </p>
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={addStep}
            className="w-full py-3 border-2 border-dashed border-[#cbd5e1] rounded-xl text-[#64748b] hover:border-[#6366f1] hover:text-[#6366f1] transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            Adım Ekle
          </button>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-[#6366f1] text-white rounded-lg hover:bg-[#4f46e5] disabled:opacity-50 font-medium flex items-center gap-2"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Kaydet
          </button>
          <button
            onClick={() => setEditing(null)}
            className="px-6 py-2.5 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc] font-medium"
          >
            Vazgeç
          </button>
        </div>

        {confirmDialog}
      </div>
    );
  }

  // ----------------------------------------------------------------- liste
  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-1">Otomasyonlar</h1>
          <p className="text-[#64748b]">
            Tetikleyiciye bağlı, koşullu mail akışları
          </p>
        </div>
        <button
          onClick={startCreate}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#6366f1] text-white rounded-lg hover:bg-[#4f46e5] font-medium"
        >
          <Plus size={18} />
          Yeni Otomasyon
        </button>
      </div>

      {templates.length === 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          Otomasyon kurmak için önce en az bir <strong>şablon</strong> oluşturmalısınız.
        </div>
      )}

      {automations.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-[#e2e8f0]">
          <Workflow size={64} className="mx-auto text-[#cbd5e1] mb-4" />
          <h3 className="text-lg font-bold text-[#0f172a] mb-2">Henüz otomasyon yok</h3>
          <p className="text-[#64748b] max-w-md mx-auto">
            Örneğin: listeye eklenen kişiye <em>hemen</em> hoş geldin maili, <em>3 gün sonra</em>{' '}
            açmadıysa hatırlatma, <em>7 gün sonra</em> tıkladıysa teklif.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {automations.map((a) => (
            <div key={a.id} className="bg-white rounded-xl border border-[#e2e8f0] p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-bold text-[#0f172a]">{a.name}</h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        a.enabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {a.enabled ? '● Çalışıyor' : '○ Duraklatıldı'}
                    </span>
                  </div>
                  {a.description && <p className="text-sm text-[#64748b]">{a.description}</p>}
                  <p className="text-xs text-[#94a3b8] mt-1">
                    {a.trigger?.type === 'recipient_added'
                      ? `Tetikleyici: Yeni alıcı eklendiğinde${
                          a.trigger.group ? ` (grup: ${a.trigger.group})` : ''
                        }`
                      : 'Tetikleyici: Elle ekleme'}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEnrollTarget(a)}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc]"
                    title="Mevcut alıcıları akışa ekle"
                  >
                    <UserPlus size={15} />
                    Alıcı Ekle
                  </button>
                  <button
                    onClick={() => handleToggle(a)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg font-medium ${
                      a.enabled
                        ? 'border border-[#e2e8f0] hover:bg-[#f8fafc]'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {a.enabled ? <Pause size={15} /> : <Play size={15} />}
                    {a.enabled ? 'Duraklat' : 'Başlat'}
                  </button>
                  <button
                    onClick={() => setEditing({ ...a, trigger: a.trigger || {} })}
                    className="px-3 py-2 text-sm border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc]"
                  >
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDelete(a)}
                    className="p-2 text-[#94a3b8] hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Adım zinciri */}
              <div className="flex items-center gap-2 flex-wrap mb-4 p-3 bg-[#f8fafc] rounded-lg">
                {(a.steps || []).map((step, i) => (
                  <React.Fragment key={step.id || i}>
                    {i > 0 && <ChevronRight size={16} className="text-[#cbd5e1]" />}
                    <div className="px-3 py-2 bg-white rounded-lg border border-[#e2e8f0] text-sm">
                      <div className="flex items-center gap-1.5 text-[#0f172a] font-medium">
                        <Mail size={13} className="text-[#6366f1]" />
                        {templateName(step.template_id)}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-[#64748b] mt-0.5">
                        <Clock size={11} />
                        {describeDelay(step, i)}
                        {step.condition && step.condition !== 'always' && (
                          <span className="text-[#8b5cf6]">
                            · {CONDITIONS.find((c) => c.value === step.condition)?.label}
                          </span>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              {/* İstatistik */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Akışta', value: a.stats?.active || 0, color: 'text-[#2563eb]' },
                  { label: 'Tamamlanan', value: a.stats?.completed || 0, color: 'text-[#10b981]' },
                  { label: 'Çıkarılan', value: a.stats?.stopped || 0, color: 'text-[#64748b]' },
                  { label: 'Gönderilen mail', value: a.stats?.emails_sent || 0, color: 'text-[#8b5cf6]' },
                ].map((s) => (
                  <div key={s.label} className="p-3 bg-[#f8fafc] rounded-lg">
                    <p className="text-xs text-[#64748b]">{s.label}</p>
                    <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Alıcı ekleme penceresi */}
      {enrollTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-[#0f172a] mb-1">Akışa alıcı ekle</h3>
            <p className="text-sm text-[#64748b] mb-4">
              &quot;{enrollTarget.name}&quot; akışına hangi alıcılar eklensin?
            </p>

            <select
              value={enrollPreset}
              onChange={(e) => setEnrollPreset(e.target.value)}
              className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg bg-white mb-4"
            >
              {presets.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>

            <p className="text-xs text-[#94a3b8] mb-4 flex items-start gap-1.5">
              <Users size={14} className="shrink-0 mt-0.5" />
              Zaten akışta olan, abonelikten çıkmış veya engellenmiş adresler atlanır.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleEnroll}
                className="flex-1 px-4 py-2 bg-[#6366f1] text-white rounded-lg hover:bg-[#4f46e5] font-medium"
              >
                Ekle
              </button>
              <button
                onClick={() => setEnrollTarget(null)}
                className="flex-1 px-4 py-2 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc]"
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDialog}
    </div>
  );
};

export default Automations;
